import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/contexts/TenantContext';
import { MASTER_TENANT_ID } from '@/types/tenant';

export interface CoachingTemplate {
  id: string;
  name: string;
  description: string | null;
  category: 'sales' | 'strategy' | 'people' | 'communication' | 'education' | 'professional_dev' | 'professional_development';
  template_type: string;
  icon: string | null;
  estimated_duration: number;
  output_document: string;
  is_default: boolean;
  is_active: boolean;
  intake_script: any;
  document_template: any;
  scoring_criteria: any;
  created_at: string;
  updated_at: string;
  // AI Avatar configuration
  anam_persona_id: string | null;
  platform: 'anam' | 'elevenlabs' | null;
  elevenlabs_agent_id: string | null;
  system_prompt: string | null;
  document_format: string | null;
}

export const useCoachingTemplates = () => {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.tenant_id;
  const isMasterTenant = !currentTenantId || currentTenantId === MASTER_TENANT_ID;
  
  return useQuery({
    queryKey: ['coaching-templates', currentTenantId],
    queryFn: async () => {
      let query = supabase
        .from('coaching_templates')
        .select('*')
        .eq('is_active', true);
      
      // Tenant isolation - white-label tenants see system templates + their own
      if (!isMasterTenant && currentTenantId) {
        query = query.or(`tenant_id.eq.${MASTER_TENANT_ID},tenant_id.eq.${currentTenantId}`);
      }
      
      const { data, error } = await query
        .order('category', { ascending: true })
        .order('name', { ascending: true });
        
      if (error) throw error;
      return data as CoachingTemplate[];
    }
  });
};

export const useCoachingTemplate = (templateId?: string) => {
  return useQuery({
    queryKey: ['coaching-template', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      
      const { data, error } = await supabase
        .from('coaching_templates')
        .select('*')
        .eq('id', templateId)
        .maybeSingle();
        
      if (error) throw error;
      return data as CoachingTemplate | null;
    },
    enabled: !!templateId
  });
};
